/**
 * IndexedDB Database
 *
 * Uses Dexie.js for IndexedDB access.
 * Schema mirrors the Android app's Room database.
 *
 * CRITICAL: All song queries must filter by whitelist!
 */

import Dexie, { type Table } from 'dexie';
import type { ArtistWhitelistEntry } from '@zemer/shared';

// ============================================================================
// Entity Types
// ============================================================================

export interface SongEntity {
  id: string;
  title: string;
  thumbnails: string; // JSON stringified
  albumId?: string;
  albumName?: string;
  duration?: number;
  liked?: boolean;
  inLibrary?: number; // timestamp
  isDownloaded?: boolean;
}

export interface ArtistEntity {
  id: string;
  name: string;
  thumbnails?: string;
  description?: string;
  subscriberCount?: string;
  bookmarkedAt?: number;
}

export interface AlbumEntity {
  id: string;
  title: string;
  thumbnails?: string;
  year?: string;
  trackCount?: number;
  bookmarkedAt?: number;
}

export interface PlaylistEntity {
  id: string;
  name: string;
  thumbnails?: string;
  description?: string;
  songCount?: number;
  bookmarkedAt?: number;
  isLocal?: boolean;
}

export interface SongArtistMap {
  songId: string;
  artistId: string;
}

export interface SongAlbumMap {
  songId: string;
  albumId: string;
}

export interface PlaylistSongMap {
  id?: number;
  playlistId: string;
  songId: string;
  position: number;
}

export interface PlayEventEntity {
  id?: number;
  songId: string;
  timestamp: number;
  playDuration?: number;
}

export interface DownloadEntity {
  songId: string;
  blob?: Blob;
  downloadedAt: number;
  fileSize?: number;
}

export interface ThumbnailCacheEntity {
  url: string; // Original URL as key
  data: string; // Base64 or cached URL
  cachedAt: number;
}

export interface LikedSongEntity {
  id: string;
  title: string;
  artistsJson: string; // JSON stringified artists array
  thumbnail?: string;
  thumbnailsJson?: string; // JSON stringified thumbnails array
  likedAt: number;
}

export interface AppSettingsEntity {
  key: string;
  value: string; // JSON stringified value
}

// ============================================================================
// Database Class
// ============================================================================

class ZemerDatabase extends Dexie {
  songs!: Table<SongEntity, string>;
  artists!: Table<ArtistEntity, string>;
  albums!: Table<AlbumEntity, string>;
  playlists!: Table<PlaylistEntity, string>;
  artistWhitelist!: Table<ArtistWhitelistEntry, string>;
  songArtistMap!: Table<SongArtistMap, [string, string]>;
  songAlbumMap!: Table<SongAlbumMap, [string, string]>;
  playlistSongMap!: Table<PlaylistSongMap, number>;
  playEvents!: Table<PlayEventEntity, number>;
  downloads!: Table<DownloadEntity, string>;
  thumbnailCache!: Table<ThumbnailCacheEntity, string>;
  likedSongs!: Table<LikedSongEntity, string>;
  appSettings!: Table<AppSettingsEntity, string>;

  constructor() {
    super('ZemerDB');

    this.version(1).stores({
      songs: 'id, albumId, liked, inLibrary, isDownloaded',
      artists: 'id, name, bookmarkedAt',
      albums: 'id, title, bookmarkedAt',
      playlists: 'id, name, bookmarkedAt, isLocal',
      artistWhitelist: 'artistId, isFemale, isKidZone, isIsraeli',
      songArtistMap: '[songId+artistId], songId, artistId',
      songAlbumMap: '[songId+albumId], songId, albumId',
      playlistSongMap: '++id, playlistId, songId, position',
      playEvents: '++id, songId, timestamp',
      downloads: 'songId',
    });

    // Version 2: Add new category indexes matching Android app
    this.version(2).stores({
      songs: 'id, albumId, liked, inLibrary, isDownloaded',
      artists: 'id, name, bookmarkedAt',
      albums: 'id, title, bookmarkedAt',
      playlists: 'id, name, bookmarkedAt, isLocal',
      artistWhitelist: 'artistId, isFemale, isChasid, isGenZ, isKids, isKidZone, isIsraeli',
      songArtistMap: '[songId+artistId], songId, artistId',
      songAlbumMap: '[songId+albumId], songId, albumId',
      playlistSongMap: '++id, playlistId, songId, position',
      playEvents: '++id, songId, timestamp',
      downloads: 'songId',
    });

    // Version 3: Add thumbnail cache, liked songs, and app settings tables
    this.version(3).stores({
      songs: 'id, albumId, liked, inLibrary, isDownloaded',
      artists: 'id, name, bookmarkedAt',
      albums: 'id, title, bookmarkedAt',
      playlists: 'id, name, bookmarkedAt, isLocal',
      artistWhitelist: 'artistId, isFemale, isChasid, isGenZ, isKids, isKidZone, isIsraeli',
      songArtistMap: '[songId+artistId], songId, artistId',
      songAlbumMap: '[songId+albumId], songId, albumId',
      playlistSongMap: '++id, playlistId, songId, position',
      playEvents: '++id, songId, timestamp',
      downloads: 'songId',
      thumbnailCache: 'url, cachedAt',
      likedSongs: 'id, likedAt',
      appSettings: 'key',
    });
  }
}

export const db = new ZemerDatabase();

// ============================================================================
// Query Helpers with Whitelist Filtering
// ============================================================================

/**
 * Get songs that are in the library AND have whitelisted artists
 * CRITICAL: Always filter by whitelist
 */
export async function getLibrarySongs(): Promise<SongEntity[]> {
  // Get all whitelisted artist IDs
  const whitelistIds = await db.artistWhitelist.toCollection().primaryKeys();
  const whitelistSet = new Set(whitelistIds);

  // Get song IDs that have at least one whitelisted artist
  const songArtistMaps = await db.songArtistMap.toArray();
  const allowedSongIds = new Set<string>();

  for (const map of songArtistMaps) {
    if (whitelistSet.has(map.artistId)) {
      allowedSongIds.add(map.songId);
    }
  }

  // Query songs that are in library AND allowed
  return db.songs
    .filter((song) => song.inLibrary !== undefined && allowedSongIds.has(song.id))
    .toArray();
}

/**
 * Check if a song is playable (has whitelisted artist)
 */
export async function isSongPlayable(songId: string): Promise<boolean> {
  const whitelistIds = await db.artistWhitelist.toCollection().primaryKeys();
  const whitelistSet = new Set(whitelistIds);

  const artistMaps = await db.songArtistMap.where('songId').equals(songId).toArray();

  return artistMaps.some((map) => whitelistSet.has(map.artistId));
}

/**
 * Get bookmarked artists (only whitelisted ones)
 */
export async function getBookmarkedArtists(): Promise<ArtistEntity[]> {
  const whitelistIds = await db.artistWhitelist.toCollection().primaryKeys();
  const whitelistSet = new Set(whitelistIds);

  return db.artists
    .filter((artist) => artist.bookmarkedAt !== undefined && whitelistSet.has(artist.id))
    .toArray();
}
